import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { HttpRequest } from '@angular/common/http';
import { loadingExclude } from './loading.exclude';
import { utils } from 'src/app/modules/libs/utils';

@Injectable({
  providedIn: 'root'
})
export class LoadingService {
  private httpRequests = 0;
  private manualRequests = 0;
  private requestTimings = new Map<string, number>(); // start times
  private _loading$ = new BehaviorSubject<boolean>(false);

  public readonly loading$ = this._loading$.asObservable();
  public isHome = true;

  private _showLoading?: boolean = true;
  private _active?: boolean = false;

  constructor(
    private zone: NgZone,
    private router: Router
  ) { }

  // ---- Automatic (HTTP) ----
  startHttpRequest(req: HttpRequest<any>) {
    const shouldExclude = loadingExclude.some(ex => req?.urlWithParams.toLowerCase().includes(ex.toLowerCase()));
    this.requestTimings.set(req.urlWithParams, Date.now());
    if (this.showLoading && !shouldExclude)
      this.httpRequests++;
    this.updateLoading(req, true, shouldExclude ? '✔️' : '🚫');
  }

  endHttpRequest(req: HttpRequest<any>, size: number = 0) {
    const shouldExclude = loadingExclude.some(ex => req?.urlWithParams.toLowerCase().includes(ex.toLowerCase()));
    if (!shouldExclude && this.httpRequests > 0) {
      this.httpRequests--;
    }

    let elapsedText = '';
    const start = this.requestTimings.get(req.urlWithParams);
    if (start) {
      const elapsed = Date.now() - start;
      elapsedText = `⏱️ ${elapsed} ms  📦 [${utils.formatBytes(size)}]`;
      this.requestTimings.delete(req.urlWithParams);
    }
    else {
      elapsedText = `📦 [${utils.formatBytes(size)}]`;
    }

    this.updateLoading(req, false, shouldExclude ? '✔️' : '🚫', elapsedText);
  }

  // ---- Manual ----
  show() {
    this.manualRequests++;
    const req = new HttpRequest<any>("OPTIONS", "MANUAL");
    this.updateLoading(req, true, 'START');
  }

  hide() {
    if (this.manualRequests > 0) {
      this.manualRequests--;
    }
    const req = new HttpRequest<any>("OPTIONS", "MANUAL");
    this.updateLoading(req, false, 'END');
  }

  // ---- Internal ----
  private updateLoading(req?: HttpRequest<any>, start?: boolean, what?: string, elapsedText: string = '') {
    // Get current route
    const currentUrl = this.router.url;
    this.isHome = currentUrl === '/' || currentUrl.startsWith('/home') || currentUrl === '/login';
    this.setActive(this.httpRequests > 0 || this.manualRequests > 0);
    this.zone.run(() => this._loading$.next(this.active));
    this.consoleLog(req, start, what, elapsedText);
  }

  consoleLog(req?: HttpRequest<any>, start?: boolean, what?: string, elapsedText: string = '') {
    console.log(
      `LoadingService: ${this.httpRequests} HTTP requests, ${this.manualRequests} manual requests, loading = ${this.active} ${what ?? ''} %c${start ? '▶' : '⏹'} %c${req?.method ?? ''} ${req ? `[${req.urlWithParams}]` : ''} ${elapsedText}`,
      `color:${start ? '#00c853' : '#ff1744'}; font-weight:bold;`,
      ''
    );
  }

  setShowLoading(value: boolean) {
    this._showLoading = value;
  }

  get showLoading(): boolean | undefined {
    return this._showLoading;
  }

  setActive(value: boolean) {
    this._active = value;
  }

  get active(): boolean {
    return this._active ?? false;
  }
}
